import {forwardRef} from "react";
import FancySelect from "../FancySelect";
import Avatar, {AvatarWithName} from "../Avatar";
import {accountUsers, projectsUsers} from "../../lib/utils";
import {sortBy} from "lodash";
import {Col, Row, DSTag} from "@cdx/ds";

const HistoryButton = forwardRef(({onChange, user}, ref) => (
  <DSTag ref={ref} onClick={() => onChange(user)}>
    <Row sp="4px" align="center">
      <Avatar user={user} size={16} style={{marginTop: -2, marginBottom: -2}} />
      <span>{user.name}</span>
    </Row>
  </DSTag>
));

const History = forwardRef(({value, projects, root, onChange, deckId}, ref) => {
  const projectIds = projects.filter((p) => p.$meta.isLoaded).map((p) => p.id);
  const userId = root.loggedInUser.$meta.get("id", null);

  const getAssignements = (rel, constraints) =>
    projectIds.length && userId
      ? root.account.$meta.find(rel, {
          assigneeId: {op: "neq", value: userId},
          assignee: {withProjectAccess: {projectId: projectIds}},
          assignedById: userId,
          ...constraints,
          $order: "-lastAssignedAt",
          $limit: 5,
        })
      : [];

  const genericHistory = getAssignements("assigneeAssignments");
  const deckHistory = getAssignements("assigneeDeckAssignments", {deckId});
  let allUsers = [root.loggedInUser, ...[...deckHistory, ...genericHistory].map((h) => h.assignee)];
  const userIdSet = new Set();
  const uniqUsers = allUsers
    .filter((u) => {
      if (value && value.id === u.id) return false;
      if (userIdSet.has(u.id)) return false;
      userIdSet.add(u.id);
      return true;
    })
    .slice(0, 5);
  if (!uniqUsers.length) return null;
  return (
    <Row sp="4px" wrap>
      {uniqUsers.map((assignee, index) => (
        <HistoryButton
          key={assignee.$meta.get("id", index)}
          user={assignee}
          ref={index === 0 ? ref : null}
          onChange={onChange}
        />
      ))}
    </Row>
  );
});

const UserSelectWithHistory = forwardRef((props, ref) => {
  const {onChange, value, excludeIds, onClose, projects, root, allowObservers, deckId, autoFocus} =
    props;
  const userList = !projects
    ? accountUsers(root.account, {allowObservers})
    : projects.length === 0
      ? [root.loggedInUser]
      : projectsUsers(root.account, projects, {allowObservers});

  return (
    <Col sp="8px">
      <FancySelect
        getOptions={() =>
          sortBy(excludeIds ? userList.filter((u) => !excludeIds.includes(u.id)) : userList, (u) =>
            (u.name || "").toLowerCase()
          )
        }
        extractKey={(u) => u.id}
        getSearchText={(u) => u.name}
        onChange={onChange}
        value={value}
        ref={ref}
        onClose={onClose}
        autoFocus={autoFocus}
      >
        {(u) => <AvatarWithName user={u} />}
      </FancySelect>
      <History
        value={value}
        projects={projects || []}
        root={root}
        onChange={onChange}
        deckId={deckId}
        ref={ref}
      />
    </Col>
  );
});

export default UserSelectWithHistory;
